import { dashboardApi } from '@/store/api/base/api.base';
import { walkPages } from '@/store/api/base/api.walk';
import type { TypeInvoice, TypeInvoicePayment, TypeInvoiceReminder } from '@/types/commercial/types.commercial.invoice';
import type { TypeDocumentPdf } from '@/types/document/types.document.print';
import type { TypeAppDispatch } from '@/store/store';
import { dropById, upsertById } from '@/store/api/base/api.cache';
import { PDF_REQUEST_TIMEOUT_MS } from '@/config/document/document.pdf.config';

type TypeInvoiceFile = { fileName: string; xml: string };

const PAGE_SIZE = 200;
const MAX_PAGES = 40;

const byIssued = (a: TypeInvoice, b: TypeInvoice) =>
    b.issuedAt.localeCompare(a.issuedAt) || b.number.seq - a.number.seq;

export const invoiceApi = dashboardApi.injectEndpoints({
    endpoints: (build) => ({
        readInvoicePdf: build.mutation<TypeDocumentPdf, string>({
            query: (id) => ({ url: `/invoices/${id}/pdf`, timeout: PDF_REQUEST_TIMEOUT_MS }),
        }),

        listInvoices: build.query<TypeInvoice[], void>({
            queryFn: async () => {
                const walked = await walkPages<TypeInvoice>({
                    url: '/invoices',
                    fallback: 'Non è stato possibile caricare le fatture',
                    params: { sort: 'issued' },
                    pageSize: PAGE_SIZE,
                    maxPages: MAX_PAGES,
                });
                if ('error' in walked) return walked;
                const items = walked.data;

                return { data: items };
            },
            providesTags: ['Invoice'],
        }),

        readInvoice: build.query<TypeInvoice, string>({
            query: (id) => ({ url: `/invoices/${id}` }),
            providesTags: (_result, _error, id) => [{ type: 'Invoice', id }],
        }),

        createInvoice: build.mutation<TypeInvoice, { invoice: TypeInvoice; clientRef: string }>({
            query: ({ invoice, clientRef }) => ({ url: '/invoices', method: 'post', data: { invoice: toInput(invoice), clientRef } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    write(dispatch, data);
                } catch {
                }
            },
        }),

        updateInvoice: build.mutation<TypeInvoice, { id: string; invoice: TypeInvoice }>({
            query: ({ id, invoice }) => ({ url: `/invoices/${id}`, method: 'patch', data: { invoice: toInput(invoice) } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    write(dispatch, data);
                } catch {
                }
            },
        }),

        issueInvoice: build.mutation<TypeInvoice, string>({
            query: (id) => ({ url: `/invoices/${id}/issue`, method: 'post' }),
            onQueryStarted: async (_id, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    write(dispatch, data);
                } catch {
                }
            },
        }),

        cancelInvoice: build.mutation<TypeInvoice, { id: string; note?: string }>({
            query: ({ id, note }) => ({ url: `/invoices/${id}/cancel`, method: 'post', data: { note: note ?? '' } }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    write(dispatch, data);
                } catch {
                }
            },
        }),

        duplicateInvoice: build.mutation<TypeInvoice, string>({
            query: (id) => ({ url: `/invoices/${id}/duplicate`, method: 'post' }),
            onQueryStarted: async (_id, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    write(dispatch, data);
                } catch {
                }
            },
        }),

        removeInvoice: build.mutation<null, string>({
            query: (id) => ({ url: `/invoices/${id}`, method: 'delete' }),
            onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
                try {
                    await queryFulfilled;
                    dispatch(invoiceApi.util.updateQueryData('listInvoices', undefined, (draft) => {
                        dropById(draft, id);
                    }));
                } catch {
                }
            },
        }),

        addInvoicePayment: build.mutation<TypeInvoice, { id: string; payment: Omit<TypeInvoicePayment, 'id'> }>({
            query: ({ id, payment }) => ({ url: `/invoices/${id}/payments`, method: 'post', data: payment }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    write(dispatch, data);
                } catch {
                }
            },
        }),

        removeInvoicePayment: build.mutation<TypeInvoice, { id: string; paymentId: string }>({
            query: ({ id, paymentId }) => ({ url: `/invoices/${id}/payments/${paymentId}`, method: 'delete' }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    write(dispatch, data);
                } catch {
                }
            },
        }),

        addInvoiceReminder: build.mutation<TypeInvoice, { id: string; reminder: Omit<TypeInvoiceReminder, 'id'> }>({
            query: ({ id, reminder }) => ({ url: `/invoices/${id}/reminders`, method: 'post', data: reminder }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    write(dispatch, data);
                } catch {
                }
            },
        }),

        readInvoiceXml: build.query<TypeInvoiceFile, string>({
            query: (id) => ({ url: `/invoices/${id}/xml` }),
            keepUnusedDataFor: 0,
        }),

        transmitInvoice: build.mutation<TypeInvoice, string>({
            query: (id) => ({ url: `/invoices/${id}/transmit`, method: 'post' }),
            onQueryStarted: async (_id, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    write(dispatch, data);
                } catch {
                }
            },
        }),

    }),
});

const write = (dispatch: TypeAppDispatch, invoice: TypeInvoice) => {
    dispatch(invoiceApi.util.updateQueryData('listInvoices', undefined, (draft) => {
        upsertById(draft, invoice);
        draft.sort(byIssued);
    }));
    dispatch(invoiceApi.util.updateQueryData('readInvoice', invoice.id, () => invoice));
};

const toInput = (invoice: TypeInvoice) => {
    const {
        id, number, status, recipient, sdi, payments, reminders, history, paidAt, createdAt, updatedAt, recurringId, periodKey, ...input
    } = invoice;
    return input;
};

export const {
    useReadInvoicePdfMutation,
    useListInvoicesQuery,
    useReadInvoiceQuery,
    useLazyReadInvoiceXmlQuery,
    useTransmitInvoiceMutation,
    useCreateInvoiceMutation,
    useUpdateInvoiceMutation,
    useIssueInvoiceMutation,
    useCancelInvoiceMutation,
    useDuplicateInvoiceMutation,
    useRemoveInvoiceMutation,
    useAddInvoicePaymentMutation,
    useRemoveInvoicePaymentMutation,
    useAddInvoiceReminderMutation,
} = invoiceApi;

export default invoiceApi;
